import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Clock, MapPin, CheckCircle2, XCircle } from 'lucide-react';
import Logo from '../../components/Logo';

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function PublicBookingPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    axios.get(`${BASE}/api/public/book/${token}`)
      .then((r) => {
        if (r.data.error) setError(r.data.error);
        else setData(r.data);
      })
      .catch(() => setError('network'));
  }, [token]);

  useEffect(() => {
    if (!data?.expires_at) return;
    const tick = () => {
      const ms = new Date(data.expires_at).getTime() - Date.now();
      if (ms <= 0) { setTimeLeft(0); return; }
      setTimeLeft(ms);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [data]);

  async function claim() {
    setClaiming(true);
    try {
      const r = await axios.post(`${BASE}/api/public/book/${token}`);
      setResult(r.data);
    } catch (e) {
      setResult({ error: 'network' });
    } finally {
      setClaiming(false);
    }
  }

  function fmtDateTime(d) {
    return new Date(d).toLocaleString('de-AT', { dateStyle: 'long', timeStyle: 'short' });
  }

  if (error || result?.error) {
    const code = error || result?.error;
    const messages = {
      invalid_token: { title: 'Ungültiger Link', body: 'Dieser Buchungslink existiert nicht.' },
      token_used: { title: 'Bereits verwendet', body: 'Dieser Link wurde bereits eingelöst.' },
      token_expired: { title: 'Abgelaufen', body: 'Dieser Buchungslink ist leider abgelaufen.' },
      already_taken: { title: 'Termin bereits vergeben', body: 'Dieser Termin wurde bereits vergeben. Sie bleiben in der Warteliste.' },
      network: { title: 'Netzwerkfehler', body: 'Bitte versuchen Sie es erneut.' },
    };
    const m = messages[code] || { title: 'Fehler', body: code };
    return (
      <Shell>
        <div className="text-center" data-testid="booking-error">
          <div className="mx-auto w-14 h-14 rounded-full bg-danger/10 text-danger flex items-center justify-center mb-4">
            <XCircle size={28}/>
          </div>
          <h1 className="text-2xl font-bold">{m.title}</h1>
          <p className="mt-2 text-text-secondary">{m.body}</p>
        </div>
      </Shell>
    );
  }

  if (result?.ok) {
    return (
      <Shell>
        <div className="text-center" data-testid="booking-success">
          <div className="mx-auto w-14 h-14 rounded-full bg-success/10 text-success flex items-center justify-center mb-4">
            <CheckCircle2 size={28}/>
          </div>
          <h1 className="text-2xl font-bold">Termin bestätigt!</h1>
          {result.slot && (
            <p className="mt-3 text-text">
              <span className="font-semibold">{fmtDateTime(result.slot.starts_at)}</span><br/>
              <span className="text-text-secondary">{data?.practice?.name}</span>
            </p>
          )}
          <p className="mt-4 text-sm text-text-secondary">Sie erhalten in Kürze eine Bestätigung per WhatsApp/SMS.</p>
        </div>
      </Shell>
    );
  }

  if (!data) return <Shell><p className="text-text-secondary text-sm text-center">Wird geladen…</p></Shell>;

  const min = Math.max(0, Math.floor((timeLeft || 0) / 60000));
  const sec = Math.max(0, Math.floor(((timeLeft || 0) % 60000) / 1000));

  return (
    <Shell practice={data.practice}>
      <div data-testid="booking-page">
        <h1 className="text-2xl font-bold tracking-tight" data-testid="booking-hero">
          {data.kind === 'pull_forward' ? 'Frueherer Termin verfuegbar' : 'Termin verfuegbar'}
        </h1>
        <p className="mt-2 text-text-secondary">
          Guten Tag {data.patient_first_name || ''}, bei {data.practice?.name} ist folgender Termin verfügbar:
        </p>

        <div className="so-card mt-6 p-5">
          <div className="flex items-center gap-3 text-lg font-semibold">
            <Clock size={20} className="text-accent" />
            <span data-testid="booking-datetime">{fmtDateTime(data.slot?.starts_at)}</span>
          </div>
          {data.slot?.duration_min && (
            <p className="mt-2 text-sm text-text-secondary">Dauer: {data.slot.duration_min} Minuten</p>
          )}
          {data.practice?.address && (
            <p className="mt-2 text-sm text-text-secondary flex items-center gap-1.5">
              <MapPin size={14}/> {data.practice.address}
            </p>
          )}
        </div>

        <div className="so-card mt-4 p-4 bg-warning/5 border-warning/30">
          <p className="text-xs uppercase tracking-wide text-warning font-semibold">Hinweis</p>
          <p className="mt-1 text-sm text-text">
            Der erste „Ja, ich nehme den Termin"-Klick bekommt den Termin. Sie haben{' '}
            <span className="font-mono font-bold" data-testid="booking-timeleft">{String(min).padStart(2, '0')}:{String(sec).padStart(2, '0')}</span> Zeit.
          </p>
        </div>

        <button
          onClick={claim}
          disabled={claiming || timeLeft === 0}
          className="so-btn so-btn-primary w-full mt-6 text-base py-3"
          data-testid="booking-claim-btn"
        >
          {claiming ? 'Wird gebucht…' : 'Ja, ich nehme den Termin'}
        </button>
      </div>
    </Shell>
  );
}

function Shell({ children, practice }) {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="px-6 py-5 border-b border-border bg-white">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <Logo variant="dark"/>
          {practice && <span className="text-sm font-semibold ml-2">{practice.name}</span>}
        </div>
      </header>
      <main className="flex-1 px-6 py-10 flex">
        <div className="max-w-xl mx-auto w-full">{children}</div>
      </main>
    </div>
  );
}
